'use client';

import { ReactNode } from 'react';

import { ArrowLeft, ArrowRight } from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

import {
  CaseStudyHeroVideo,
  type CaseStudyHeroVideoVariant,
} from './case-study-hero-video';

export interface CaseStudyStat {
  value: string;
  label: ReactNode;
}

interface CaseStudyHeroProps {
  /** Customer name shown in the eyebrow ("Customer story · {name}") */
  customerName: string;
  /** Headline – usually the result/transformation statement */
  title: ReactNode;
  /** Short paragraph below the title */
  subtitle: ReactNode;
  /** 2–4 stats shown under the video */
  stats?: CaseStudyStat[];
  /** Path to the customer story video (omit for a text-only hero) */
  videoSrc?: string;
  /** Optional frame to seek to as the static poster */
  videoStartAt?: number;
  /**
   * - `cinematic` (default): click-to-play with sound + hover controls.
   * - `ambient`: silent autoplay loop, no controls (use for brand/logo videos).
   */
  videoVariant?: CaseStudyHeroVideoVariant;
}

export const CaseStudyHero = ({
  customerName,
  title,
  subtitle,
  stats,
  videoSrc,
  videoStartAt,
  videoVariant = 'cinematic',
}: CaseStudyHeroProps) => {
  const { t, locale } = useTranslation();
  const customerStoryLabel = locale === 'ar' ? 'قصة عميل' : 'Customer story';

  return (
    <section className="bg-[#fafaf9] pt-32 pb-12 md:pt-40 md:pb-16">
      <div className="container">
        <div className="mb-10 max-w-3xl md:mb-14">
          <Link
            href="/customer-stories"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900"
          >
            {locale === 'ar' ? (
              // In RTL, "back" is a right-pointing arrow.
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            ) : (
              <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
            )}
            {t.customerStories['All case studies']}
          </Link>

          <div className="mt-8">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {customerStoryLabel} · {customerName}
            </p>
            <h1 className="font-serif text-balance text-neutral-900 text-[2rem] md:text-4xl xl:text-5xl">
              {title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {subtitle}
            </p>
          </div>
        </div>
      </div>

      {videoSrc && (
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 md:px-8">
          <CaseStudyHeroVideo
            src={videoSrc}
            startAt={videoStartAt}
            variant={videoVariant}
          />
        </div>
      )}

      {stats && stats.length > 0 && (
        <div className="container mt-12 md:mt-16">
          <div
            className="mx-auto grid max-w-3xl gap-8 border-y border-neutral-200 py-10"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                stats.length,
                3,
              )}, minmax(0, 1fr))`,
            }}
          >
            {stats.map((stat, i) => (
              <div
                key={`${stat.value}-${i}`}
                className="text-center sm:ltr:text-left sm:rtl:text-right"
              >
                <div className="font-sans text-4xl font-medium text-neutral-900 md:text-5xl">
                  {stat.value}
                </div>
                <div className="mt-1 text-sm text-neutral-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default CaseStudyHero;
